/**
 * Sound Service - เล่นเสียงแจ้งเตือนสำหรับรายการฝาก-ถอน
 */

import { merchantApi } from '../api/config';

interface SoundSettings {
  sound_deposit_url: string;
  sound_withdrawal_url: string;
  sound_enabled: string;
}

class SoundService {
  private depositSound: HTMLAudioElement | null = null;
  private withdrawalSound: HTMLAudioElement | null = null;
  private settings: SoundSettings | null = null;
  private isEnabled: boolean = true;
  private isLoaded: boolean = false;
  private hasUserInteracted: boolean = false;

  constructor() {
    this.setupUserInteractionListener();
  }

  /**
   * Setup listener for user interaction (required for autoplay)
   */
  private setupUserInteractionListener() {
    const enableAudio = () => {
      this.hasUserInteracted = true;
      // Remove listeners after first interaction
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('keydown', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
      console.log('🔊 Audio playback enabled by user interaction');
    };

    document.addEventListener('click', enableAudio);
    document.addEventListener('keydown', enableAudio);
    document.addEventListener('touchstart', enableAudio);
  }

  /**
   * Load sound settings from API
   * ใช้ public endpoint เพื่อให้ทั้ง merchant และ admin ใช้ได้
   */
  async loadSettings(): Promise<void> {
    if (this.isLoaded) {
      console.log('🔊 Sound settings already loaded');
      return;
    }

    try {
      const response = await merchantApi.get('/admin/system/sound-settings');

      if (response.data.success) {
        this.settings = response.data.data;
        const saved = localStorage.getItem('admin_sound_enabled');
        this.isEnabled = saved !== null ? saved !== 'false' : this.settings?.sound_enabled !== 'false';
        
        // Preload audio files
        if (this.settings?.sound_deposit_url) {
          this.depositSound = new Audio(this.settings.sound_deposit_url);
          this.depositSound.preload = 'auto';
          this.depositSound.onerror = () => {
            console.warn('⚠️ Failed to preload deposit sound');
          };
        }
        
        if (this.settings?.sound_withdrawal_url) {
          this.withdrawalSound = new Audio(this.settings.sound_withdrawal_url);
          this.withdrawalSound.preload = 'auto';
          this.withdrawalSound.onerror = () => {
            console.warn('⚠️ Failed to preload withdrawal sound');
          };
        }

        this.isLoaded = true;
        console.log('🔊 Sound settings loaded:', this.settings);
      }
    } catch (error) {
      console.error('Error loading sound settings:', error);
      this.isLoaded = true;
    }
  }

  /**
   * Play deposit sound
   */
  async playDepositSound(): Promise<void> {
    // Auto-load settings if not loaded
    if (!this.isLoaded) {
      await this.loadSettings();
    }

    if (!this.hasUserInteracted) {
      console.log('🔇 Cannot play sound: user has not interacted with page yet');
      return;
    }

    if (!this.isEnabled) {
      console.log('🔇 Sound is disabled');
      return;
    }

    if (!this.settings?.sound_deposit_url) {
      console.log('🔇 No deposit sound URL configured');
      return;
    }

    try {
      if (this.depositSound) {
        this.depositSound.currentTime = 0;
        await this.depositSound.play();
        console.log('🔊 Playing deposit sound');
      } else {
        const audio = new Audio(this.settings.sound_deposit_url);
        await audio.play();
        console.log('🔊 Playing deposit sound (new audio)');
      }
    } catch (err: any) {
      // NotAllowedError = browser blocked autoplay (user hasn't interacted yet)
      if (err?.name === 'NotAllowedError') {
        console.log('🔇 Sound blocked by browser - please click on the page first');
        this.hasUserInteracted = false; // Reset flag
      } else {
        console.error('Error playing deposit sound:', err);
      }
    }
  }

  /**
   * Play withdrawal sound
   */
  async playWithdrawalSound(): Promise<void> {
    // Auto-load settings if not loaded
    if (!this.isLoaded) {
      await this.loadSettings();
    }

    if (!this.hasUserInteracted) {
      console.log('🔇 Cannot play sound: user has not interacted with page yet');
      return;
    }

    if (!this.isEnabled) {
      console.log('🔇 Sound is disabled');
      return;
    }

    if (!this.settings?.sound_withdrawal_url) {
      console.log('🔇 No withdrawal sound URL configured');
      return;
    }

    try {
      if (this.withdrawalSound) {
        this.withdrawalSound.currentTime = 0;
        await this.withdrawalSound.play();
        console.log('🔊 Playing withdrawal sound');
      } else {
        const audio = new Audio(this.settings.sound_withdrawal_url);
        await audio.play();
        console.log('🔊 Playing withdrawal sound (new audio)');
      }
    } catch (err: any) {
      // NotAllowedError = browser blocked autoplay (user hasn't interacted yet)
      if (err?.name === 'NotAllowedError') {
        console.log('🔇 Sound blocked by browser - please click on the page first');
        this.hasUserInteracted = false; // Reset flag
      } else {
        console.error('Error playing withdrawal sound:', err);
      }
    }
  }

  /**
   * Toggle sound on/off
   */
  toggleSound(enabled: boolean): void {
    this.isEnabled = enabled;
    console.log('🔊 Sound', enabled ? 'enabled' : 'disabled');
  }

  /**
   * Check if sound is enabled
   */
  isSoundEnabled(): boolean {
    return this.isEnabled && this.hasUserInteracted;
  }

  /**
   * Check if settings are loaded
   */
  isSettingsLoaded(): boolean {
    return this.isLoaded;
  }

  /**
   * Refresh settings (after upload)
   */
  async refreshSettings(): Promise<void> {
    this.isLoaded = false;
    this.depositSound = null;
    this.withdrawalSound = null;
    await this.loadSettings();
  }
}

// Export singleton instance
export const soundService = new SoundService();
export default soundService;
